"use strict";

import { createApp } from "vue";
import axios from "axios";
import tokenService from "@/utils/tokenService";

// Full config
if (!process.env.VUE_APP_TESTCAFE_MODE) {
  axios.defaults.baseURL = process.env.VUE_APP_BASE_API_URL;
}
axios.defaults.headers.common["x-api-key"] = process.env.VUE_APP_API_KEY;
axios.defaults.headers.post["Content-Type"] =
  "application/x-www-form-urlencoded";

let config = {};

const app = createApp();
const _axios = axios.create(config);
app.config.globalProperties.$http = axios;

// Request interceptor
axios.interceptors.request.use(
  function(config) {
    // Use tokenService to get token with validation
    const token = tokenService.getToken();

    if (token) {
      // Keep your existing format or switch to Bearer if backend expects it
      config.headers.Authorization = "Token token=" + token;
      // For Bearer format: config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

// Response interceptor with improved error handling
axios.interceptors.response.use(
  response => response,
  error => {
    // Ignore aborted/timeout errors (they should not trigger logout)
    if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
      return Promise.reject(error);
    }

    // If there is no server response (network error), don't trigger logout here
    if (!error.response) {
      console.error('Network error (no response):', error);
      return Promise.reject(error);
    }

    // Log response body for diagnosis (remove after debugging)
    console.debug('API error response:', {
      status: error.response.status,
      data: error.response.data,
      headers: error.response.headers
    });

    const status = error.response.status;
    const body = error.response.data || '';

    // Detect auth failure:
    // - standard auth statuses OR
    // - some backends return 500 but include token / auth / expired clues in the body
    const authError =
      status === 401 || status === 419 || status === 440 ||
      (status === 500 && /token|auth|authorization|expired|invalid/i.test(JSON.stringify(body)));

    if (authError) {
      // Try normal clearAuth path (will remove tokens and try to redirect)
      try { tokenService.clearAuth(true); } catch (e) { console.error('clearAuth failed', e); }

      // Fallback: force immediate navigation to logout to stop further requests
      try {
        if (window.location.pathname !== '/logout') {
          window.location.replace('/logout');
        }
      } catch (e) {
        console.error('redirect fallback failed', e);
      }

      // Return a cancel-like rejection to suppress Uncaught (in promise)
      const cancelErr = new Error("logout_in_progress");
      cancelErr.__CANCEL__ = true;
      return Promise.reject(cancelErr);
    }

    return Promise.reject(error);
  }
);

// Remove the alert() calls from _axios interceptors
_axios.interceptors.response.use(
  function(response) {
    return response;
  },
  function(error) {
    // Let the global interceptor handle errors
    return Promise.reject(error);
  }
);

const AxiosPlugin = {
  install(app, options) {
    if (!options) {
      options = {};
    }
  }
};

export default AxiosPlugin;
